import { useEffect, useRef } from "react";
import L from "@/lib/leaflet-setup";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

const FR = {
  draw: {
    toolbar: {
      actions: { title: "Annuler le dessin", text: "Annuler" }, finish: { title: "Terminer le dessin", text: "Terminer" },
      undo: { title: "Supprimer le dernier point", text: "Supprimer le dernier point" },
      buttons: { polygon: "Dessiner un polygone", rectangle: "Dessiner un rectangle", marker: "Placer un point" },
    },
    handlers: {
      polygon: { tooltip: { start: "Cliquez pour commencer le polygone.", cont: "Cliquez pour continuer le polygone.", end: "Cliquez sur le premier point pour fermer le polygone." } },
      polyline: { error: "<strong>Erreur :</strong> les côtés ne peuvent pas se croiser !", tooltip: { start: "Cliquez pour commencer.", cont: "Cliquez pour continuer.", end: "Cliquez sur le dernier point pour terminer." } },
      rectangle: { tooltip: { start: "Cliquez et faites glisser pour dessiner un rectangle." } },
      simpleshape: { tooltip: { end: "Relâchez la souris pour terminer." } },
      marker: { tooltip: { start: "Cliquez sur la carte pour placer le point." } },
    },
  },
};
const EN = JSON.parse(JSON.stringify(L.drawLocal));
const merge = (a, b) => { Object.keys(b).forEach((k) => (typeof b[k] === "object" ? merge(a[k], b[k]) : (a[k] = b[k]))); };

const STYLE = { color: "#D52B3B", weight: 2, fillOpacity: 0.25 };
const SAT_COLOR = { pass: "#059669", review: "#D97706", fail: "#E11D48", pending: "#64748B" };

export default function PlotMap({ plots = [], editable = false, onDrawn, labels = {}, height = 420, testid = "plot-map", lang = "en", colorBySatellite = false }) {
  const el = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const cb = useRef(onDrawn);
  cb.current = onDrawn;

  useEffect(() => {
    merge(L.drawLocal, JSON.parse(JSON.stringify(EN)));
    if (lang === "fr") merge(L.drawLocal, FR);
    const m = L.map(el.current, { center: [20, 0], zoom: 2, worldCopyJump: true, zoomControl: false });
    L.control.zoom(lang === "fr" ? { zoomInTitle: "Zoom avant", zoomOutTitle: "Zoom arrière" } : {}).addTo(m);
    const street = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" });
    const sat = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19, attribution: "Esri World Imagery" });
    street.addTo(m);
    L.control.layers({ [labels.street || "Map"]: street, [labels.satellite || "Satellite"]: sat }).addTo(m);
    layer.current = L.featureGroup().addTo(m);
    if (editable) {
      m.addControl(new L.Control.Draw({
        draw: { polygon: { allowIntersection: false, showArea: true, shapeOptions: STYLE }, marker: true, polyline: false, rectangle: { shapeOptions: STYLE }, circle: false, circlemarker: false },
      }));
      m.on(L.Draw.Event.CREATED, (e) => {
        const g = e.layer.toGeoJSON().geometry;
        cb.current?.(g);
      });
    }
    map.current = m;
    return () => m.remove();
  }, [editable, labels.street, labels.satellite, lang]);

  useEffect(() => {
    const g = layer.current;
    if (!g) return;
    g.clearLayers();
    plots.forEach((p) => {
      const st = p.satellite?.status || "pending";
      const style = colorBySatellite ? { ...STYLE, color: SAT_COLOR[st] } : STYLE;
      const lyr = p.geometry.type === "Point"
        ? L.circleMarker([p.geometry.coordinates[1], p.geometry.coordinates[0]], { radius: 7, ...style, fillOpacity: 0.8 })
        : L.geoJSON(p.geometry, { style });
      lyr.bindTooltip(`${p.name} · ${p.area_ha} ha${colorBySatellite ? ` · satellite ${st}` : ""}`);
      g.addLayer(lyr);
    });
    if (plots.length) map.current.fitBounds(g.getBounds().pad(0.3), { maxZoom: 15 });
  }, [plots, colorBySatellite]);

  return <div ref={el} style={{ height }} className="z-0 w-full overflow-hidden rounded-xl border border-slate-200" data-testid={testid} />;
}
